import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import PedidoCard from '../PedidoCard';
import {
    AnalysisHeaderIcon,
    ProductionHeaderIcon,
    ReadyHeaderIcon,
    CompletedHeaderIcon,
    AnalysisEmptyIllustration,
    ProductionEmptyIllustration,
    ReadyEmptyIllustration,
    CompletedEmptyIllustration
} from './KanbanIllustrations';
import {
    ColumnWrapper,
    ColumnHeader,
    ColumnTitleWrapper,
    ColumnIcon,
    ColumnTitle,
    ColumnCount,
    ColumnContent,
    EmptyState,
    EmptyIllustrationWrapper,
    EmptyTitle,
    EmptyDescription,
    ConfigSection
} from './KanbanColumn.styles';

// Map column types to status keys
const columnStatusMap = {
    'containerAnalise': 'analysis',
    'containerProducao': 'production',
    'containerPronto': 'ready',
    'containerFinalizado': 'completed'
};

// Header icons for each status (custom SVG)
const statusHeaderIcons = {
    analysis: AnalysisHeaderIcon,
    production: ProductionHeaderIcon,
    ready: ReadyHeaderIcon,
    completed: CompletedHeaderIcon
};

// Empty state illustrations (custom SVG)
const emptyIllustrations = {
    analysis: AnalysisEmptyIllustration,
    production: ProductionEmptyIllustration,
    ready: ReadyEmptyIllustration,
    completed: CompletedEmptyIllustration
};

// Status colors passed to illustrations
const statusColors = {
    analysis: '#8b5cf6',
    production: '#f59e0b',
    ready: '#10b981',
    completed: '#06b6d4'
};

const KanbanColumn = memo(({
    title,
    count,
    pedidos,
    emptyMessage,
    headerContent,
    containerClass,
    headerClass,
    onShowPedido,
    onAddEntregador,
    onAvancar,
    showAvancarButton = true,
    isUpdating = false,
    pedidosProcessando,
    animationDelay = '0ms'
}) => {
    const { t } = useTranslation('orders');
    const status = columnStatusMap[containerClass] || 'analysis';
    const HeaderIcon = statusHeaderIcons[status];
    const EmptyIllustration = emptyIllustrations[status];
    const color = statusColors[status];

    // Determine button status for advance action
    const getButtonStatus = () => {
        if (status === 'analysis') return 'analysis';
        if (status === 'production') return 'production';
        if (status === 'ready') return 'ready';
        return 'analysis';
    };

    return (
        <ColumnWrapper $animationDelay={animationDelay}>
            <ColumnHeader $status={status}>
                <ColumnTitleWrapper>
                    <ColumnIcon $status={status}>
                        <HeaderIcon color={color} />
                    </ColumnIcon>
                    <ColumnTitle>{title}</ColumnTitle>
                </ColumnTitleWrapper>
                <ColumnCount $status={status} $hasOrders={count > 0}>
                    {count}
                </ColumnCount>
            </ColumnHeader>

            {/* Config section for analysis column */}
            {headerContent && (
                <ConfigSection>
                    {headerContent}
                </ConfigSection>
            )}

            <ColumnContent>
                {count === 0 ? (
                    <EmptyState>
                        <EmptyIllustrationWrapper $status={status}>
                            <EmptyIllustration color={color} size={72} />
                        </EmptyIllustrationWrapper>
                        <EmptyTitle>{t('emptyStates.noOrders')}</EmptyTitle>
                        <EmptyDescription>{emptyMessage}</EmptyDescription>
                    </EmptyState>
                ) : (
                    pedidos.map((pedido, index) => (
                        <PedidoCard
                            key={pedido._id}
                            pedido={pedido}
                            onShowPedido={onShowPedido}
                            onAddEntregador={onAddEntregador}
                            onAvancar={onAvancar}
                            showAvancarButton={showAvancarButton}
                            isUpdating={isUpdating}
                            pedidosProcessando={pedidosProcessando}
                            buttonStatus={getButtonStatus()}
                            animationDelay={`${index * 50}ms`}
                        />
                    ))
                )}
            </ColumnContent>
        </ColumnWrapper>
    );
});

KanbanColumn.displayName = 'KanbanColumn';

export default KanbanColumn;
